import { useEffect } from 'react';

import { useFormEditorDialogStore } from './useFormEditorDialogStore';

const FORM_EDITOR_ANCHOR_ATTRIBUTE = 'data-form-editor-anchor';

/**
 * Marks a rendered field or section row as a scroll anchor. Spread onto the
 * element that should be brought into view for that id.
 */
export const formEditorAnchorProps = (id: string): Record<string, string> => ({
  [FORM_EDITOR_ANCHOR_ATTRIBUTE]: id,
});

/**
 * Scrolls the row registered for `id` into view. Returns false when no such
 * row is currently rendered (e.g. its section is collapsed).
 */
export const scrollFormEditorAnchorIntoView = (
  id: string,
  options: ScrollIntoViewOptions = { block: 'center' }
): boolean => {
  // Field ids are JSON-pointer-ish (`#/properties/Owner`), so quote the value
  // rather than interpolating it bare into the selector.
  const element = document.querySelector(
    `[${FORM_EDITOR_ANCHOR_ATTRIBUTE}=${JSON.stringify(id)}]`
  );
  if (!element) {
    return false;
  }
  element.scrollIntoView(options);

  return true;
};

/**
 * Consumes a pending scroll-anchor request once the form editor list is on
 * screen. `active` must be true only while the list is actually rendered.
 */
export const useFormEditorScrollAnchor = (active: boolean): void => {
  const scrollAnchorId = useFormEditorDialogStore((s) => s.scrollAnchorId);
  const clearScrollAnchor = useFormEditorDialogStore(
    (s) => s.clearScrollAnchor
  );

  useEffect(() => {
    if (!active || !scrollAnchorId) {
      return;
    }

    // The list is remounted from scratch on the way back from a sub-editor, so
    // wait for the browser to lay it out before measuring anything.
    const frame = requestAnimationFrame(() => {
      scrollFormEditorAnchorIntoView(scrollAnchorId);
      clearScrollAnchor();
    });

    return () => cancelAnimationFrame(frame);
  }, [active, scrollAnchorId, clearScrollAnchor]);
};
